import './App.css';
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ChakraProvider } from '@chakra-ui/react';
import theme from './theme';
import { AuthProvider } from './contexts/AuthContext';
import { WorkspaceProvider } from './contexts/WorkspaceContext';
import { ColorPreferenceProvider } from './contexts/ColorPreferenceContext';
import { PreferencesProvider } from './contexts/PreferencesContext';
import { DisplayCurrencyProvider } from './contexts/DisplayCurrencyContext';
import ErrorBoundary from './components/shared/ErrorBoundary';
import useIsPhone from './hooks/useIsPhone';
import EnvBadge from './components/shared/EnvBadge';

// Auth components
import LoginPage from './components/auth/LoginPage';
import CleanroomLogin from './components/auth/CleanroomLogin';
import SignupPage from './components/auth/SignupPage';
import ProtectedRoute from './components/auth/ProtectedRoute';
import PublicRoute from './components/auth/PublicRoute';

// Layout
import ProtectedLayout from './components/layout/ProtectedLayout';
import OnboardingPage from './components/onboarding/OnboardingPage';

// Pages
import ListMovements from './components/movements/ListMovements';
import Dashboard from './components/dashboard/Dashboard';
import AccountsPage from './components/accounts/AccountsPage';
import CategoriesPage from './components/categories/CategoriesPage';
import TagsPage from './components/tags/TagsPage';
import GoalsPage from './components/goals/GoalsPage';
import SourceFilesPage from './components/source-files/SourceFilesPage';
import StatementCheckupPage from './components/repairs/checkup/StatementCheckupPage';
import ChatPage from './components/chat/ChatPage';
import InvitationAcceptPage from './components/invitations/InvitationAcceptPage';
import MembersPage from './components/members/MembersPage';

/**
 * `/` opens on Home on a phone and on Movements, the working surface, on a
 * computer. The query travels with it: `?new=1` opens quick add.
 */
function RootRedirect() {
  const { search } = useLocation();
  const isPhone = useIsPhone();
  if (isPhone) return <Navigate to={{ pathname: '/dashboard', search }} replace />;
  // Nothing on the desktop reads `new`, and the filters would carry it into
  // every URL after this one.
  const params = new URLSearchParams(search);
  params.delete('new');
  const rest = params.toString();
  return <Navigate to={{ pathname: '/movements', search: rest ? `?${rest}` : '' }} replace />;
}

function App() {
  return (
    <ChakraProvider theme={theme}>
      <ColorPreferenceProvider>
      <ErrorBoundary>
      <EnvBadge />
      <Router>
        <AuthProvider>
          <PreferencesProvider>
          <WorkspaceProvider>
          <DisplayCurrencyProvider>
          <Routes>
            {/* No /callback route: Google returns to the backend, which
                verifies the code and redirects straight into the app. */}
            <Route path="/signup" element={<SignupPage />} />
            {process.env.REACT_APP_USE_CLEANROOM_AUTH === 'true' && (
              <Route path="/login_cleanroom" element={<CleanroomLogin />} />
            )}

            {/* Invitation accept — accessible signed-in or signed-out; the page handles redirect. */}
            <Route path="/invitations/accept" element={<InvitationAcceptPage />} />

            {/* Rutas públicas */}
            <Route element={<PublicRoute />}>
              <Route path="/login" element={<LoginPage />} />
            </Route>

            {/* Rutas protegidas con layout compartido */}
            <Route element={<ProtectedRoute />}>
              {/* Outside ProtectedLayout on purpose: onboarding is a full page
                  with no sidebar or header, and keeping it out of the layout is
                  also what stops the layout's redirect from looping. */}
              <Route path="/onboarding" element={<OnboardingPage />} />
              <Route path="/" element={<RootRedirect />} />
              <Route element={<ProtectedLayout />}>
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/movements" element={<ListMovements />} />
                <Route path="/chat" element={<ChatPage />} />
                <Route path="/accounts" element={<AccountsPage />} />
                <Route path="/categories" element={<CategoriesPage />} />
                <Route path="/tags" element={<TagsPage />} />
                <Route path="/goals" element={<GoalsPage />} />
                <Route path="/members" element={<MembersPage />} />
                <Route path="/currencies" element={<Navigate to="/movements" replace />} />
                <Route path="/source-files" element={<SourceFilesPage />} />
                <Route path="/source-files/checkup" element={<StatementCheckupPage />} />
                <Route path="/statements" element={<Navigate to="/source-files" replace />} />
              </Route>
            </Route>
          </Routes>
          </DisplayCurrencyProvider>
          </WorkspaceProvider>
          </PreferencesProvider>
        </AuthProvider>
      </Router>
      </ErrorBoundary>
      </ColorPreferenceProvider>
    </ChakraProvider>
  );
}

export default App;
